import React from "react";
import { Checkbox, IconButton, Typography } from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import UndoIcon from "@material-ui/icons/Undo";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import styled from "styled-components";
import { CONTENT_COLUMN, weekdayInitial } from "../utils/classPlanGrid";
import {
  LESSON_STATE,
  getLessonState,
  needsLessons
} from "../utils/classPlanEditing";

const CONTENT_COLUMN_MIN_WIDTH = 320;
const DANGER_COLOR = "#EE4848";
const PRIMARY_COLOR = "#1C1F74";
const WARNING_COLOR = "#ff9800";
const WARNING_BACKGROUND = "#fff3e0";
const WARNING_ROW_STYLE = { background: WARNING_BACKGROUND };
const LESSON_COLUMN_WIDTH = 44;
const EMPTY_DAY_COLUMN_WIDTH = 28;
const NO_LESSON_BACKGROUND = "#f2f2f2";

const DayHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  line-height: 1.1;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
`;

const LessonHeader = styled.span`
  display: block;
  width: 100%;
  text-align: center;
  font-size: 10px;
  color: rgba(0, 0, 0, 0.5);
`;

const RowAction = styled(IconButton)`
  && {
    flex-shrink: 0;
    padding: 4px;
    color: rgba(0, 0, 0, 0.45);
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    transition: opacity 0.15s, color 0.15s;
  }
  &&:hover,
  &&:focus-visible {
    color: ${({ $hoverColor }) => $hoverColor};
  }
`;

const ContentCell = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;

  .InovuaReactDataGrid__row:hover & ${RowAction}, & ${RowAction}:focus-visible {
    opacity: 1;
  }
`;

const ContentText = styled(Typography)`
  && {
    flex: 1;
    font-size: 13px;
    line-height: 1.3;
    white-space: normal;
    word-break: break-word;
  }
`;

const DeletedText = styled(ContentText)`
  && {
    color: rgba(0, 0, 0, 0.38);
    text-decoration: line-through;
  }
`;

const MissingLessonsIcon = styled(WarningRoundedIcon)`
  && {
    flex-shrink: 0;
    color: ${WARNING_COLOR};
  }
`;

const DraftPlaceholder = styled(ContentText)`
  && {
    color: rgba(0, 0, 0, 0.6);
    font-weight: 500;
    cursor: text;
  }
`;

const NO_LESSON_HEADER_PROPS = { style: { background: NO_LESSON_BACKGROUND } };

const dayNumber = date => String(Number(date.slice(8, 10)));

function buildDayGroup(day, locale, noLessonLabel) {
  const hasLessons = day.lessons.length > 0;
  return {
    name: day.date,
    header: (
      <DayHeader title={hasLessons ? day.date : noLessonLabel}>
        <span>{weekdayInitial(day.date, locale)}</span>
        <strong>{dayNumber(day.date)}</strong>
      </DayHeader>
    ),
    ...(hasLessons ? {} : { headerProps: NO_LESSON_HEADER_PROPS })
  };
}

function buildEmptyDayColumn(day, noLessonLabel) {
  return {
    name: `day-${day.date}`,
    group: day.date,
    header: "",
    defaultWidth: EMPTY_DAY_COLUMN_WIDTH,
    minWidth: EMPTY_DAY_COLUMN_WIDTH,
    resizable: false,
    showColumnMenuTool: false,
    editable: false,
    headerProps: { ...NO_LESSON_HEADER_PROPS, title: noLessonLabel },
    onRender: cellProps => {
      // eslint-disable-next-line no-param-reassign
      cellProps.style = {
        ...(cellProps.style || {}),
        background: NO_LESSON_BACKGROUND
      };
    },
    render: () => null
  };
}

function buildLessonColumn(day, lesson, { editable, owners, onToggleLesson }) {
  const showTime = day.lessons.length > 1;
  return {
    name: `lesson-${lesson.id}`,
    group: day.date,
    header: showTime ? <LessonHeader>{lesson.startTime}</LessonHeader> : "",
    defaultWidth: LESSON_COLUMN_WIDTH,
    minWidth: LESSON_COLUMN_WIDTH,
    resizable: false,
    showColumnMenuTool: false,
    editable: false,
    textAlign: "center",
    headerProps: {
      title: [lesson.startTime, lesson.endTime].filter(Boolean).join(" - ")
    },
    render: ({ data }) => {
      const state = getLessonState(data, lesson.id, owners);
      return (
        <Checkbox
          size="small"
          color="primary"
          disabled={
            !editable || data.isDeleted || state === LESSON_STATE.BLOCKED
          }
          checked={
            data.isDeleted
              ? data.lessonIds.includes(lesson.id)
              : state === LESSON_STATE.CHECKED
          }
          onChange={() => onToggleLesson(data, lesson.id)}
          style={{ padding: 2 }}
        />
      );
    }
  };
}

const stopPropagation = event => event.stopPropagation();

function renderRowAction(data, { editable, deleteLabel, undoLabel, onToggleDelete }) {
  if (!editable || data.isNew) return null;

  const label = data.isDeleted ? undoLabel : deleteLabel;
  return (
    <RowAction
      size="small"
      aria-label={label}
      title={label}
      $visible={data.isDeleted}
      $hoverColor={data.isDeleted ? PRIMARY_COLOR : DANGER_COLOR}
      onMouseDown={stopPropagation}
      onClick={event => {
        event.stopPropagation();
        onToggleDelete(data.id);
      }}
    >
      {data.isDeleted ? (
        <UndoIcon fontSize="small" />
      ) : (
        <DeleteOutlineIcon fontSize="small" />
      )}
    </RowAction>
  );
}

function renderContent({ data }, options) {
  if (data.isNew && !data.text) {
    return (
      <DraftPlaceholder component="div">
        {options.draftPlaceholder}
      </DraftPlaceholder>
    );
  }

  const Text = data.isDeleted ? DeletedText : ContentText;
  return (
    <ContentCell>
      {needsLessons(data) && (
        <MissingLessonsIcon
          fontSize="small"
          titleAccess={options.missingLessonsLabel}
        />
      )}
      <Text component="div" title={data.text}>
        {data.text}
      </Text>
      {renderRowAction(data, options)}
    </ContentCell>
  );
}

function highlightMissingLessons(cellProps, { data }) {
  if (!needsLessons(data)) return;
  // eslint-disable-next-line no-param-reassign
  cellProps.style = {
    ...(cellProps.style || {}),
    background: WARNING_BACKGROUND,
    boxShadow: `inset 4px 0 0 ${WARNING_COLOR}`
  };
}

const getRowStyle = ({ data }) =>
  needsLessons(data) ? WARNING_ROW_STYLE : undefined;

export function buildClassPlanGrid(
  days,
  {
    contentLabel,
    noLessonLabel,
    draftPlaceholder,
    deleteLabel,
    undoLabel,
    missingLessonsLabel,
    locale,
    editable,
    owners,
    onToggleLesson,
    onToggleDelete
  }
) {
  const contentOptions = {
    draftPlaceholder,
    editable,
    deleteLabel,
    undoLabel,
    missingLessonsLabel,
    onToggleDelete
  };
  const contentColumn = {
    name: CONTENT_COLUMN,
    header: contentLabel,
    locked: "start",
    defaultFlex: 1,
    minWidth: CONTENT_COLUMN_MIN_WIDTH,
    showColumnMenuTool: false,
    editable: (value, { data }) => editable && !data.isDeleted,
    onRender: highlightMissingLessons,
    render: cellProps => renderContent(cellProps, contentOptions)
  };

  const lessonOptions = { editable, owners, onToggleLesson };
  const dayColumns = days.flatMap(day =>
    day.lessons.length
      ? day.lessons.map(lesson => buildLessonColumn(day, lesson, lessonOptions))
      : [buildEmptyDayColumn(day, noLessonLabel)]
  );

  return {
    columns: [contentColumn, ...dayColumns],
    groups: days.map(day => buildDayGroup(day, locale, noLessonLabel)),
    rowStyle: getRowStyle
  };
}
